import { Link } from "react-router-dom";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/hooks/useCart";
import { useThemeColor, hexAlpha } from "@/hooks/useThemeColor";
import { useSectionBg } from "@/hooks/useSectionBg";
import { useDesignTokens } from "@/hooks/useDesignTokens";

/* Category icon mapping */
const categoryIcon: Record<string, string> = {
  PS5: "ri-gamepad-line",
  PS4: "ri-gamepad-line",
  PC: "ri-computer-line",
  Controllers: "ri-gamepad-fill",
  Headsets: "ri-headphone-line",
};

const categoryLabel: Record<string, string> = {
  PS5: "جهاز PS5",
  PS4: "جهاز PS4",
  PC: "كمبيوتر ألعاب",
  Controllers: "يد تحكم",
  Headsets: "سماعات",
};

/* Skeleton card while loading */
function SkeletonRow() {
  return (
    <div className="flex flex-col lg:flex-row items-center gap-12 animate-pulse">
      <div className="w-full lg:w-1/2 h-72 md:h-96 rounded-2xl bg-white/5" />
      <div className="w-full lg:w-1/2 flex flex-col gap-4 items-end">
        <div className="h-4 w-24 rounded-full bg-white/5" />
        <div className="h-10 w-64 rounded-xl bg-white/5" />
        <div className="h-4 w-full rounded bg-white/5" />
        <div className="h-4 w-3/4 rounded bg-white/5" />
        <div className="grid grid-cols-2 gap-3 w-full mt-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 rounded-xl bg-white/5" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DevicesSection() {
  const tc = useThemeColor();
  const dt = useDesignTokens();
  const { bg: sectionBg } = useSectionBg('sectionDevices');
  const { products, loading } = useProducts();
  const { addItem, items } = useCart();

  /* Show all products (consoles + accessories) */
  const displayProducts = products;

  const isInCart = (id: number) => items.some((i) => i.id === id);

  const handleAdd = (product: typeof products[0]) => {
    addItem({
      id: product.id,
      nameAr: product.nameAr,
      price: product.price,
      image: product.images?.[0] ?? "",
      category: product.category,
    });
  };

  return (
    <section id="devices" className="py-24 px-4" style={{ background: sectionBg }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.4em] uppercase mb-4"
            style={{ fontFamily: dt.font, color: hexAlpha(tc.primary, 0.7) }}>
            الأجهزة المتاحة
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-5"
            style={{ fontFamily: dt.font, filter: `drop-shadow(0 0 30px ${hexAlpha(tc.primary, 0.15)})` }}>
            الأجهزة
          </h2>
          <div className="w-20 h-0.5 mx-auto"
            style={{ background: `linear-gradient(to right, transparent, ${tc.primary}, transparent)` }} />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-24">
          {loading ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : displayProducts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center bg-[#111] border border-white/5 rounded-2xl mx-auto mb-4">
                <i className="ri-inbox-line text-3xl text-gray-700"></i>
              </div>
              <p className="text-gray-500 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                لا توجد منتجات حالياً
              </p>
            </div>
          ) : (
            displayProducts.map((product, idx) => {
              const inCart = isInCart(product.id);
              const disc = product.originalPrice > product.price
                ? Math.round((1 - product.price / product.originalPrice) * 100)
                : 0;
              /* Show top 4 highlights OR top 4 specs as feature chips */
              const chips = product.highlights?.length
                ? product.highlights.slice(0, 4)
                : product.specs?.slice(0, 4).map((s) => `${s.label}: ${s.value}`) ?? [];

              return (
                <div
                  key={product.id}
                  className={`flex flex-col lg:flex-row items-center gap-12 ${idx % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
                >
                  {/* ── Image ── */}
                  <div className="w-full lg:w-1/2 relative">
                    <Link
                      to={`/product/${product.id}`}
                      className="block relative overflow-hidden w-full h-72 md:h-96 border cursor-pointer group/img"
                      style={{
                        borderColor: hexAlpha(tc.primary, 0.12),
                        boxShadow: `0 0 60px ${hexAlpha(tc.primary, 0.06)}`,
                        background: "#0d0d0d",
                        borderRadius: dt.radius,
                      }}
                    >
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.nameAr}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover/img:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#111]">
                          <i className="ri-image-line text-gray-700 text-5xl"></i>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <span className="opacity-0 group-hover/img:opacity-100 transition-all duration-300 bg-black/70 backdrop-blur-sm text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-2"
                          style={{ fontFamily: "'Tajawal', sans-serif", border: `1px solid ${hexAlpha(tc.primary, 0.3)}`, color: tc.primary }}>
                          <i className="ri-eye-line text-sm"></i>
                          عرض المنتج
                        </span>
                      </div>

                      {/* Corner accents */}
                      <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 rounded-tr-2xl pointer-events-none"
                        style={{ borderColor: hexAlpha(tc.primary, 0.3) }} />
                      <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 rounded-bl-2xl pointer-events-none"
                        style={{ borderColor: hexAlpha(tc.primary, 0.3) }} />

                      {/* Discount badge */}
                      {disc > 0 && (
                        <span className="absolute top-4 left-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full"
                          style={{ fontFamily: "'Tajawal', sans-serif" }}>
                          خصم {disc}%
                        </span>
                      )}

                      {/* Badge */}
                      {product.badge && (
                        <span className="absolute top-4 right-4 text-xs font-bold px-3 py-1 rounded-full text-black"
                          style={{ background: tc.primary, fontFamily: "'Tajawal', sans-serif" }}>
                          {product.badge}
                        </span>
                      )}
                    </Link>
                    {/* Glow */}
                    <div className="absolute -inset-4 rounded-3xl blur-2xl -z-10"
                      style={{ background: hexAlpha(tc.primary, 0.04) }} />
                  </div>

                  {/* ── Info ── */}
                  <div className="w-full lg:w-1/2 text-right">
                    {/* Category tag */}
                    <span
                      className="inline-flex items-center gap-2 border text-xs font-semibold px-4 py-1.5 mb-5 tracking-wide"
                      style={{
                        background: hexAlpha(tc.primary, 0.1),
                        borderColor: hexAlpha(tc.primary, 0.2),
                        color: tc.primary,
                        fontFamily: dt.font,
                        borderRadius: dt.radiusFull,
                      }}
                    >
                      <i className={`${categoryIcon[product.category] ?? "ri-gamepad-line"} text-xs`}></i>
                      {categoryLabel[product.category] ?? product.category}
                    </span>

                    {/* Name */}
                    <h3
                      className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight"
                      style={{ fontFamily: dt.font, filter: `drop-shadow(0 0 20px ${hexAlpha(tc.primary, 0.1)})` }}
                    >
                      {product.nameAr}
                    </h3>

                    {/* Rating */}
                    <div className="flex items-center gap-2 justify-end mb-4">
                      <span className="text-gray-500 text-xs" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                        ({product.reviews} تقييم)
                      </span>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-sm"
                            style={{ color: i < product.rating ? tc.primary : "#374151" }} />
                        ))}
                      </div>
                    </div>

                    {/* Short desc */}
                    {product.shortDesc && (
                      <p className="text-gray-400 text-base mb-6 leading-relaxed"
                        style={{ fontFamily: dt.font, fontWeight: 300 }}>
                        {product.shortDesc}
                      </p>
                    )}

                    {/* Feature chips */}
                    {chips.length > 0 && (
                      <div className="grid grid-cols-2 gap-3 mb-8">
                        {chips.map((chip, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 bg-[#141414] border border-white/5 px-4 py-2.5 transition-all"
                            style={{ borderRadius: dt.radius }}
                            onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.2)}
                            onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.05)"}
                          >
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-checkbox-circle-fill text-sm" style={{ color: tc.primary }}></i>
                            </div>
                            <span className="text-gray-300 text-sm truncate" style={{ fontFamily: dt.font }}>
                              {chip}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Specs preview (first 3) */}
                    {product.specs?.length > 0 && chips === product.highlights?.slice(0, 4) && (
                      <div className="flex flex-col gap-1.5 mb-8">
                        {product.specs.slice(0, 3).map((spec, i) => (
                          <div key={i} className="flex items-center justify-end gap-3 text-sm">
                            <span className="text-gray-200 font-medium" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                              {spec.value}
                            </span>
                            <span className="text-gray-600" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                              {spec.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Price + CTA */}
                    <div className="flex flex-wrap items-center gap-4">
                      <div>
                        <p className="text-gray-500 text-xs mb-1" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                          السعر
                        </p>
                        <div className="flex items-baseline gap-2">
                          <p className="font-bold text-3xl"
                            style={{
                              fontFamily: "'Amiri', 'Tajawal', serif",
                              background: `linear-gradient(to left, ${tc.primaryLight}, ${tc.primary}, ${tc.primaryDark})`,
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                              filter: `drop-shadow(0 0 12px ${hexAlpha(tc.primary, 0.3)})`,
                            }}>
                            {product.price.toLocaleString()} ر.س
                          </p>
                          {product.originalPrice > product.price && (
                            <span className="text-gray-600 text-base line-through" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                              {product.originalPrice.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Add to cart */}
                      <button
                        onClick={() => handleAdd(product)}
                        className="group relative font-bold px-7 py-3 text-sm transition-all whitespace-nowrap cursor-pointer overflow-hidden"
                        style={{
                          background: inCart ? "#10b981" : `linear-gradient(to right, ${tc.primary}, ${tc.primaryDark})`,
                          color: inCart ? "white" : tc.textOnPrimary,
                          fontFamily: dt.font,
                          borderRadius: dt.radiusFull,
                          boxShadow: inCart ? "0 0 20px rgba(16,185,129,0.25)" : `0 0 20px ${hexAlpha(tc.primary, 0.2)}`,
                        }}
                      >
                        <span className="relative z-10 flex items-center gap-2">
                          <i className={inCart ? "ri-check-line" : "ri-shopping-cart-line"}></i>
                          {inCart ? "في السلة" : "أضف للسلة"}
                        </span>
                        {!inCart && (
                          <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
                        )}
                      </button>

                      {/* Details link */}
                      <Link
                        to={`/product/${product.id}`}
                        className="flex items-center gap-2 border px-7 py-3 text-sm transition-all whitespace-nowrap cursor-pointer"
                        style={{
                          borderColor: hexAlpha(tc.primary, 0.25),
                          color: tc.primary,
                          fontFamily: dt.font,
                          borderRadius: dt.radiusFull,
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.5);
                          (e.currentTarget as HTMLElement).style.background = hexAlpha(tc.primary, 0.08);
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.25);
                          (e.currentTarget as HTMLElement).style.background = "transparent";
                        }}
                      >
                        <i className="ri-eye-line text-sm"></i>
                        التفاصيل الكاملة
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
